import { Image } from "@imagekit/react";
import { forwardRef, useState } from "react";
import { ikPath } from "../Pages/CommonPages/ImageKit";
import defaultProductImage from "../assets/images/default-product.png";

// Unified image: ImageKit-served URLs go through the SDK <Image> (responsive srcSet, q-auto/f-auto,
// lazy). Local bundled assets / external hosts fall through to a plain <img> so they don't get
// mis-routed through the CDN endpoint. `priority` maps to eager loading + high fetch priority.
const OptimizedImage = forwardRef(
  (
    {
      src,
      alt = "",
      width = 96,
      height = 96,
      className = "",
      loading = "lazy",
      priority = false,
      Header, // legacy prop; no longer branches on it. Kept out of the DOM spread.
      layout, // NextUI-ism from some callers; not a valid <img> attribute.
      ...props
    },
    ref,
  ) => {
    const [errored, setErrored] = useState(false);
    if (!src) return null;
    const eager = priority || loading === "eager";
    const path = ikPath(src);
    const w = typeof width === "string" ? parseInt(width) : width;
    const h = typeof height === "string" ? parseInt(height) : height;

    // No ImageKit path (external/bundled): render the original URL as a plain <img>.
    // ImageKit fetch failed (missing/404 on the CDN): show the local placeholder instead of a
    // broken-image icon.
    if (!path || errored) {
      return (
        <img
          ref={ref}
          src={path && errored ? defaultProductImage : src}
          alt={alt}
          width={w}
          height={h}
          className={className}
          loading={eager ? "eager" : "lazy"}
          fetchpriority={eager ? "high" : "auto"}
          {...props}
        />
      );
    }

    return (
      <Image
        src={path}
        alt={alt}
        width={w}
        height={h}
        className={className}
        loading={eager ? "eager" : "lazy"}
        fetchpriority={eager ? "high" : "auto"}
        onError={() => setErrored(true)}
        {...props}
      />
    );
  },
);

OptimizedImage.displayName = "OptimizedImage";

export default OptimizedImage;
